import React, { useEffect, useState, useMemo } from 'react';
import { PageLayout } from '../../components/organisms/PageLayout';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import { SubscriptionPlan, UserSubscription } from '../../types/subscription';
import { openDodoCheckout } from '../../utils/dodo';
import { useUser } from '../../context/UserContext';
import { showToast } from '../../utils/toastManager';
import { track } from '../../utils/analytics';
import '../../styles/purchase.css';

const FREE_PLAN_CODES = ['FREE_PLAN', 'TRIAL_PLAN'];

const PLAN_FEATURES: Record<string, string[]> = {
  STARTER: [
    'Unlimited job applications',
    'Kanban pipeline board',
    'AI resume tailoring assistant',
    'Contact & recruiter tracking',
    'Job description archiving',
  ],
  PLUS: [
    'Everything in Starter',
    'Full pipeline analytics',
    'Smart follow-up drafts',
    'Interview prep with AI',
    'Priority support',
  ],
  PREMIUM: [
    'Everything in Plus',
    'Unlimited AI resume versions',
    'Salary negotiation templates',
    'Early access to new features',
    'Dedicated onboarding',
  ],
  PRO: [
    'Unlimited job applications',
    'AI resume tailoring assistant',
    'Smart follow-up drafts',
    'Contact & recruiter tracking',
    'Full pipeline analytics',
    'Job description archiving',
    'Kanban pipeline board',
  ],
  INTENSIVE: [
    'Everything in Pro',
    'Unlimited AI resume versions',
    'Interview prep with AI',
    'Salary negotiation templates',
    'Priority support',
    'Early access to new features',
  ],
};

const getPlanFeatures = (plan: SubscriptionPlan): string[] => {
  const code = plan.code.toUpperCase();
  for (const key of Object.keys(PLAN_FEATURES)) {
    if (code.includes(key)) return PLAN_FEATURES[key];
  }
  const fmt = (n: number) => (n === -1 ? 'Unlimited' : n.toLocaleString());
  return [
    `${fmt(plan.job_position_limit)} job applications`,
    `${fmt(plan.contacts_limit)} contacts`,
    `${fmt(plan.documents_limit)} documents`,
    `${fmt(plan.resumes_limit)} resumes`,
    `${fmt(plan.is_ai_tokens_limit ? plan.subscription_ai_tokens : plan.ai_tools_calls_limit)} AI ${plan.is_ai_tokens_limit ? 'tokens' : 'generations'}`,
  ];
};

const isFeatured = (plan: SubscriptionPlan) => {
  const c = plan.code.toUpperCase();
  return c.includes('PLUS') || c.includes('PRO');
};

const hasFreeTrial = (plan: SubscriptionPlan) => {
  const c = plan.code.toUpperCase();
  return c.includes('PLUS') || c.includes('PRO');
};

const periodLabel = (days: number): string => {
  if (days === 90) return '3 months';
  if (days === 180) return '6 months';
  if (days === 365) return 'Annual';
  return `${days}d`;
};

const monthlyEquiv = (price: number | string, days: number): number =>
  Math.round(Number(price) / (days / 30));

const CheckIcon: React.FC<{ featured: boolean }> = ({ featured }) => (
  <span className={`pp-check ${featured ? 'pp-check--featured' : ''}`}>
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M1.5 5L4 7.5L8.5 2.5" stroke={featured ? '#fff' : '#059669'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const Purchase: React.FC = () => {
  const { userProfile } = useUser();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [activeSubscription, setActiveSubscription] = useState<UserSubscription | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'OfferFlow | Choose Your Plan';
    track('upgrade_page_viewed');
  }, []);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const data = await api.get<UserSubscription>(API_ENDPOINTS.subscriptions.getUserSubscription);
        const planCode = data?.plan?.code ?? data?.plan_code ?? '';
        if (data && !FREE_PLAN_CODES.includes(planCode)) {
          setActiveSubscription(data);
        }
      } catch {
      }
    };
    fetchSubscription();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.get<SubscriptionPlan[]>(API_ENDPOINTS.subscriptions.getPlans);
        if (!data || data.length === 0) {
          setPlans([]);
          return;
        }
        setPlans(data.filter(p => p.code !== 'FREE_PLAN' && p.code !== 'TRIAL_PLAN'));
      } catch (err: any) {
        setError(err.message || 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const availablePeriods = useMemo<number[]>(() => {
    const set = new Set(plans.map(p => p.billing_period_days));
    return Array.from(set).sort((a, b) => a - b);
  }, [plans]);

  useEffect(() => {
    if (availablePeriods.length > 0 && selectedPeriod === null) {
      setSelectedPeriod(availablePeriods[0]);
    }
  }, [availablePeriods, selectedPeriod]);

  const baseline3m = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    plans
      .filter(p => p.billing_period_days === 90)
      .forEach(p => {
        const tierKey = p.code.replace(/_3_MONTH$|_6_MONTH$|_12_MONTH$|_ANNUAL$/, '');
        map.set(tierKey, monthlyEquiv(p.price, 90));
      });
    return map;
  }, [plans]);

  const visiblePlans = useMemo<SubscriptionPlan[]>(() => {
    if (selectedPeriod === null) return [];
    return plans.filter(p => p.billing_period_days === selectedPeriod);
  }, [plans, selectedPeriod]);

  const getSaving = (plan: SubscriptionPlan): number => {
    if (plan.billing_period_days === 90) return 0;
    const tierKey = plan.code.replace(/_3_MONTH$|_6_MONTH$|_12_MONTH$|_ANNUAL$/, '');
    const base = baseline3m.get(tierKey);
    if (!base || base === 0) return 0;
    const monthly = monthlyEquiv(plan.price, plan.billing_period_days);
    return Math.round((1 - monthly / base) * 100);
  };

  const periodSaving = useMemo<Map<number, number>>(() => {
    const map = new Map<number, number>();
    availablePeriods.forEach(days => {
      if (days === 90) { map.set(days, 0); return; }
      const savings = plans
        .filter(p => p.billing_period_days === days)
        .map(p => getSaving(p));
      map.set(days, savings.length > 0 ? Math.max(...savings) : 0);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePeriods, plans, baseline3m]);

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (processingPlan) return;

    if (!userProfile?.email) {
      showToast('Please sign in to purchase a plan', 'error');
      return;
    }
    if (!plan.dodo_price_id) {
      showToast('This plan is not available for purchase yet. Please contact support.', 'error');
      return;
    }
    try {
      setProcessingPlan(plan.code);
      track('begin_checkout', { plan: plan.code, price: Number(plan.price), currency: 'USD' });
      await openDodoCheckout(plan.code);
    } catch (err: any) {
      showToast(err.message || 'Failed to open checkout. Please try again.', 'error');
      setProcessingPlan(null);
    }
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/welcome' },
    { label: 'Settings', href: '/profile' },
    { label: 'Choose Plan' },
  ];

  if (activeSubscription) {
    return (
      <PageLayout breadcrumbItems={breadcrumbItems}>
        <div className="pp-page">
          <div className="pp-page__empty">
            <h2>You already have an active subscription</h2>
            <p>
              Plan: <strong>{activeSubscription.plan?.name ?? activeSubscription.plan_code}</strong>
              {activeSubscription.expires_at && (
                <> · Active until <strong>{new Date(activeSubscription.expires_at).toLocaleDateString()}</strong></>
              )}
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout breadcrumbItems={breadcrumbItems}>
        <div className="pp-page">
          <div className="pp-page__loading">Loading plans…</div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout breadcrumbItems={breadcrumbItems}>
        <div className="pp-page">
          <div className="pp-page__error">
            <h2>Error loading plans</h2>
            <p>{error}</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <div className="pp-page">
        <div className="pp-header">
          <h1 className="pp-header__title">Choose your plan</h1>
          <p className="pp-header__sub">
            Upgrade to unlock AI tools, unlimited applications, and more.
          </p>
        </div>

        {availablePeriods.length > 1 && (
          <div className="pp-period-toggle">
            {availablePeriods.map(days => {
              const saving = periodSaving.get(days) ?? 0;
              return (
                <button
                  key={days}
                  className={`pp-period-btn ${selectedPeriod === days ? 'pp-period-btn--active' : ''}`}
                  onClick={() => setSelectedPeriod(days)}
                >
                  {periodLabel(days)}
                  {saving > 0 && (
                    <span className="pp-period-save">Save {saving}%</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {visiblePlans.length === 0 ? (
          <div className="pp-page__empty">
            <p>No plans available at the moment. Please check back later.</p>
          </div>
        ) : (
          <div className="pp-grid" style={{ gridTemplateColumns: `repeat(${visiblePlans.length}, 1fr)` }}>
            {visiblePlans.map((plan) => {
              const featured = isFeatured(plan);
              const trial = hasFreeTrial(plan);
              const features = getPlanFeatures(plan);
              const busy = processingPlan === plan.code;
              const monthly = monthlyEquiv(plan.price, plan.billing_period_days);
              const saving = getSaving(plan);

              return (
                <div
                  key={plan.uid}
                  className={`pp-card ${featured ? 'pp-card--featured' : ''}`}
                >
                  {featured && <div className="pp-card__badge">Most popular</div>}

                  <div className="pp-card__tier">{plan.name}</div>

                  <div className="pp-card__price">
                    <span className="pp-card__currency">$</span>
                    <span className="pp-card__amount">{Number(plan.price).toFixed(0)}</span>
                    <span className="pp-card__period">/{periodLabel(plan.billing_period_days)}</span>
                  </div>

                  {plan.billing_period_days !== 30 && (
                    <div className="pp-card__monthly">
                      ${monthly}/mo
                      {saving > 0 && (
                        <span className="pp-card__save-badge">Save {saving}%</span>
                      )}
                    </div>
                  )}

                  {trial ? (
                    <div className="pp-card__trial">7-day free trial · No credit card required</div>
                  ) : (
                    <div className="pp-card__trial pp-card__trial--empty" />
                  )}

                  <div className="pp-card__divider" />

                  <ul className="pp-card__features">
                    {features.map((f, fi) => (
                      <li key={fi} className="pp-card__feature">
                        <CheckIcon featured={featured} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className="pp-card__btn"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={!!processingPlan || !plan.dodo_price_id}
                  >
                    {busy
                      ? 'Redirecting…'
                      : plan.dodo_price_id
                      ? 'Buy now'
                      : 'Coming Soon'}
                  </button>

                  {trial && plan.dodo_price_id && (
                    <p className="pp-card__note">Cancel anytime. No hidden fees.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Purchase;
